// All model parameters and system prompts are managed server-side via .env
// No frontend configuration needed

// ── Supported file types (single source of truth) ────────────────────
// Every component that checks file types should import from here.

export const ALLOWED_MIME_TYPES = {
    // Document files
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.ms-excel': 'XLS',
    'text/plain': 'TXT',
    'text/markdown': 'MD',

    // Source code files
    'text/x-python': 'PY',
    'text/javascript': 'JS/JSX',
    'text/typescript': 'TS/TSX',
    'text/html': 'HTML',
    'text/css': 'CSS',
    'text/x-scss': 'SCSS',
    'text/x-sass': 'SASS',
    'application/json': 'JSON',
    'application/xml': 'XML',
    'text/yaml': 'YAML',
    'text/x-java-source': 'JAVA',
    'text/x-c': 'C/H',
    'text/x-c++src': 'CPP',
    'text/x-c++hdr': 'HPP',
    'text/x-csharp': 'CS',
    'text/x-php': 'PHP',
    'text/x-ruby': 'RB',
    'text/x-go': 'GO',
    'text/x-rust': 'RS',
    'text/x-swift': 'SWIFT',
    'text/x-kotlin': 'KT',
    'text/x-scala': 'SCALA',
    'text/x-shellscript': 'SH/BASH',
    'text/x-sql': 'SQL',
    'text/x-r': 'R',
    'text/x-perl': 'PL',
    'text/x-lua': 'LUA',

    // Data files
    'text/csv': 'CSV',
    'text/tab-separated-values': 'TSV',
    'application/rtf': 'RTF',

    // Archive files
    'application/zip': 'ZIP',
    'application/x-zip-compressed': 'ZIP',
    'application/x-compressed': 'ZIP',
    'application/octet-stream': 'Archive',
    'application/x-7z-compressed': '7Z',

    // Image files (OCR)
    'image/png': 'PNG',
    'image/jpeg': 'JPG',
    'image/gif': 'GIF',
    'image/webp': 'WEBP',
    'image/bmp': 'BMP',
    'image/tiff': 'TIFF',

    // Audio files (transcription)
    'audio/mpeg': 'MP3',
    'audio/wav': 'WAV',
    'audio/ogg': 'OGG',
    'audio/mp4': 'M4A',
    'audio/flac': 'FLAC',
    'audio/webm': 'WEBM',
};

export const ALLOWED_EXTENSIONS = new Set([
    '.pdf', '.docx', '.pptx', '.xlsx', '.xls', '.txt', '.md',
    '.py', '.js', '.jsx', '.ts', '.tsx', '.html', '.css', '.scss', '.sass',
    '.json', '.xml', '.yaml', '.yml', '.java', '.c', '.cpp', '.h', '.hpp',
    '.cs', '.php', '.rb', '.go', '.rs', '.swift', '.kt', '.scala',
    '.sh', '.bash', '.sql', '.r', '.pl', '.lua', '.ini', '.cfg', '.conf',
    '.toml', '.env', '.csv', '.tsv', '.log', '.rtf', '.zip', '.7z',
    '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tiff', '.tif',
    '.mp3', '.wav', '.ogg', '.m4a', '.flac', '.webm',
]);

// Pre-computed accept string for <input type="file"> elements
export const FILE_INPUT_ACCEPT = Array.from(ALLOWED_EXTENSIONS).join(',');

// Helpers
export const isAllowedFile = (file) => {
    if (ALLOWED_MIME_TYPES[file.type]) return true;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    return ALLOWED_EXTENSIONS.has(ext);
};

export const isDocumentFile = (filename) => {
    const ext = '.' + filename.split('.').pop().toLowerCase();
    return ALLOWED_EXTENSIONS.has(ext);
};

// ── File category helpers (for preview/icon rendering) ───────────────

export const getFileExtension = (filename) =>
    filename?.split('.').pop()?.toLowerCase() ?? '';

export const FILE_CATEGORIES = {
    code: new Set(['py', 'js', 'jsx', 'ts', 'tsx', 'json', 'xml', 'html', 'css', 'java', 'cpp', 'c', 'h', 'scss', 'sass', 'yaml', 'yml', 'go', 'rs', 'php', 'rb', 'swift', 'kt', 'scala', 'sh', 'bash', 'sql', 'r', 'pl', 'lua']),
    spreadsheet: new Set(['xls', 'xlsx', 'csv']),
    pdf: new Set(['pdf']),
    word: new Set(['doc', 'docx']),
    presentation: new Set(['ppt', 'pptx']),
    image: new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tiff', 'tif']),
    audio: new Set(['mp3', 'wav', 'ogg', 'm4a', 'flac', 'webm']),
    archive: new Set(['zip', '7z']),
};

export const isCodeFile = (filename) => FILE_CATEGORIES.code.has(getFileExtension(filename));
export const isExcelFile = (filename) => FILE_CATEGORIES.spreadsheet.has(getFileExtension(filename));
export const isPdfFile = (filename) => FILE_CATEGORIES.pdf.has(getFileExtension(filename));
export const isWordFile = (filename) => FILE_CATEGORIES.word.has(getFileExtension(filename));
export const isPresentationFile = (filename) => FILE_CATEGORIES.presentation.has(getFileExtension(filename));
export const isImageFile = (filename) => FILE_CATEGORIES.image.has(getFileExtension(filename));
export const isAudioFile = (filename) => FILE_CATEGORIES.audio.has(getFileExtension(filename));
export const isArchiveFile = (filename) => FILE_CATEGORIES.archive.has(getFileExtension(filename));
